import React from 'react';
import PortalService from '../../../services/portal/portal.service';
import { usePortalResource } from '../usePortalResource';
import PortalSection, { Card } from '../PortalSection';
import PortalAvatar from '../PortalAvatar';
import { formatPortalDate } from '../format';

/** Patient details: the demographics allow-list and picture (no insurance or IDs, R-173). */
const PatientDetailsScreen: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data, loading, error } = usePortalResource(PortalService.getDemographics, patientId);
  const fullName = data ? `${data.firstName} ${data.lastName}`.trim() : '';
  return (
    <PortalSection title="Patient details" loading={loading} error={error}>
      {data && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card className="flex items-center gap-4 lg:col-span-2">
            <PortalAvatar name={fullName} pictureUrl={data.pictureUrl} size="xl" />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{fullName}</h2>
              {data.preferredName && <p className="text-sm text-slate-700">Goes by {data.preferredName}</p>}
            </div>
          </Card>
          <Card>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-slate-700">Date of birth</dt>
                <dd className="text-slate-900">{formatPortalDate(data.dob, false)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-700">Sex</dt>
                <dd className="text-slate-900">{data.sex || '—'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-700">Primary care provider</dt>
                <dd className="text-slate-900">
                  {data.pcp ? (
                    <>
                      {data.pcp.name}
                      {data.pcp.phone && (
                        <>
                          {' · '}
                          <a href={`tel:${data.pcp.phone}`} className="text-teal-800 underline">
                            {data.pcp.phone}
                          </a>
                        </>
                      )}
                    </>
                  ) : (
                    'Not recorded'
                  )}
                </dd>
              </div>
            </dl>
          </Card>
          <Card>
            <h2 className="mb-2 text-base font-semibold text-slate-900">Allergies</h2>
            {data.allergies.length === 0 ? (
              <p className="text-sm text-slate-700">No known allergies recorded.</p>
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-slate-800">
                {data.allergies.map((allergy, index) => (
                  <li key={`${allergy.name}-${index}`}>
                    {allergy.name}
                    {(allergy.reaction || allergy.severity) && (
                      <span className="text-sm text-slate-700">
                        {' '}({[allergy.reaction, allergy.severity].filter(Boolean).join(', ')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card>
            <h2 className="mb-2 text-base font-semibold text-slate-900">Guardians</h2>
            {data.guardians.length === 0 ? (
              <p className="text-sm text-slate-700">None recorded.</p>
            ) : (
              <ul className="space-y-1 text-slate-800">
                {data.guardians.map((g, index) => (
                  <li key={`${g.name}-${index}`}>
                    {g.name} <span className="text-sm text-slate-700">({g.relationship})</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </PortalSection>
  );
};

export default PatientDetailsScreen;
